export const TRAINING_MODE_MEANING = 'meaning';
export const TRAINING_MODE_COMPREHENSIVE = 'comprehensive';

export const TRAINING_MODES = Object.freeze([
  TRAINING_MODE_MEANING,
  TRAINING_MODE_COMPREHENSIVE,
]);

export const normalizeTrainingMode = (value, fallback = TRAINING_MODE_MEANING) => (
  value === TRAINING_MODE_COMPREHENSIVE
    ? TRAINING_MODE_COMPREHENSIVE
    : value === TRAINING_MODE_MEANING
      ? TRAINING_MODE_MEANING
      : fallback
);

const collectKnownTrainingModes = (value) => {
  const values = Array.isArray(value) ? value : value == null ? [] : [value];
  return TRAINING_MODES.filter((mode) => values.some((candidate) => normalizeTrainingMode(candidate, null) === mode));
};

export const normalizeTrainingModes = (value, fallback = [TRAINING_MODE_MEANING]) => {
  const normalized = collectKnownTrainingModes(value);
  if (normalized.length) return normalized;
  const fallbackModes = collectKnownTrainingModes(fallback);
  return fallbackModes.length ? fallbackModes : [TRAINING_MODE_MEANING];
};

export const toggleTrainingMode = (value, mode) => {
  const selected = normalizeTrainingModes(value);
  const normalized = normalizeTrainingMode(mode, null);
  if (!normalized) return selected;
  if (!selected.includes(normalized)) return normalizeTrainingModes([...selected, normalized]);
  return selected.length > 1
    ? selected.filter((candidate) => candidate !== normalized)
    : selected;
};

export const hasTrainingMode = (value, mode) => {
  const normalized = normalizeTrainingMode(mode, null);
  return Boolean(normalized && normalizeTrainingModes(value).includes(normalized));
};

export const getPrimaryTrainingMode = (value) => (
  hasTrainingMode(value, TRAINING_MODE_MEANING)
    ? TRAINING_MODE_MEANING
    : TRAINING_MODE_COMPREHENSIVE
);

export const buildTrainingModePayload = (value) => {
  const training_modes = normalizeTrainingModes(value);
  return {
    training_mode: getPrimaryTrainingMode(training_modes),
    training_modes,
  };
};

const readTrainingModeCount = (value) => {
  if (typeof value === 'number') return Number.isFinite(value) ? Math.max(0, value) : 0;
  if (value && typeof value === 'object') {
    return readTrainingModeCount(value.count ?? value.available ?? value.due_count ?? value.total);
  }
  const parsed = Number(value);
  return Number.isFinite(parsed) ? Math.max(0, parsed) : 0;
};

const modesFromCounts = (source) => {
  if (!source || typeof source !== 'object') return [];
  const candidates = [
    source.training_mode_counts,
    source.available_by_mode,
    source.available_modes,
    source.mode_counts,
    source.counts_by_mode,
  ];
  const modes = [];
  candidates.forEach((candidate) => {
    if (!candidate || typeof candidate !== 'object') return;
    TRAINING_MODES.forEach((mode) => {
      if (readTrainingModeCount(candidate[mode]) > 0 && !modes.includes(mode)) modes.push(mode);
    });
  });
  return modes;
};

const modesFromItems = (items) => {
  if (!Array.isArray(items) || !items.length) return [];
  const modes = [];
  items.forEach((item) => {
    const explicit = item?.training_modes ?? item?.trainingModes;
    const itemModes = explicit != null
      ? collectKnownTrainingModes(explicit)
      : collectKnownTrainingModes(item?.training_mode ?? item?.trainingMode);
    itemModes.forEach((mode) => {
      if (!modes.includes(mode)) modes.push(mode);
    });
  });
  return TRAINING_MODES.filter((mode) => modes.includes(mode));
};

export const getTrainingModes = (value, fallback = [TRAINING_MODE_MEANING]) => {
  if (Array.isArray(value) || typeof value === 'string') return normalizeTrainingModes(value, fallback);
  const source = value?.assignment || value;
  const explicit = source?.training_modes ?? source?.trainingModes;
  const explicitModes = collectKnownTrainingModes(explicit);
  if (explicitModes.length) return explicitModes;

  const itemModes = modesFromItems(source?.items);
  if (itemModes.length) return itemModes;

  const countModes = modesFromCounts(source);
  if (countModes.length) return countModes;

  const scalar = collectKnownTrainingModes([
    source?.training_mode,
    source?.trainingMode,
    value?.training_mode,
    value?.trainingMode,
  ]);
  return scalar.length ? scalar : normalizeTrainingModes(fallback);
};

export const getTrainingMode = (value, fallback = TRAINING_MODE_MEANING) => {
  return getPrimaryTrainingMode(getTrainingModes(value, [fallback]));
};

export const trainingModeLabelKey = (mode) => (
  normalizeTrainingMode(mode) === TRAINING_MODE_COMPREHENSIVE
    ? 'Comprehensive training'
    : 'Meaning training'
);

export const trainingModeDescriptionKey = (mode) => (
  normalizeTrainingMode(mode) === TRAINING_MODE_COMPREHENSIVE
    ? 'Hear, identify, spell, and say each meaning.'
    : 'Build reading meaning through context and guided practice.'
);

export const countTrainingModes = (items) => (items || []).reduce((counts, item) => {
  getTrainingModes(item).forEach((mode) => {
    counts[mode] += 1;
  });
  return counts;
}, { meaning: 0, comprehensive: 0 });

// An explicit launch mode is authoritative. Only infer a mode when the page
// was opened without one, such as from the ordinary Word Practice entry.
export const resolveInitialTrainingMode = (requestedMode, items = []) => {
  if (requestedMode !== null && requestedMode !== undefined && String(requestedMode).trim()) {
    return normalizeTrainingMode(requestedMode);
  }
  const counts = countTrainingModes(items);
  return counts.comprehensive > 0 && counts.meaning === 0
    ? TRAINING_MODE_COMPREHENSIVE
    : TRAINING_MODE_MEANING;
};

const readCount = (value) => {
  if (typeof value === 'number') return Number.isFinite(value) ? Math.max(0, value) : 0;
  if (value && typeof value === 'object') {
    return readCount(value.count ?? value.available ?? value.due_count ?? value.total);
  }
  const parsed = Number(value);
  return Number.isFinite(parsed) ? Math.max(0, parsed) : 0;
};

const pickModeCounts = (source) => {
  if (!source || typeof source !== 'object') return null;
  const candidates = [
    source.available_by_mode,
    source.available_modes,
    source.mode_counts,
    source.training_mode_counts,
    source.counts_by_mode,
  ];
  const counts = { meaning: 0, comprehensive: 0 };
  let found = false;
  candidates.forEach((candidate) => {
    if (!candidate || typeof candidate !== 'object') return;
    found = true;
    counts.meaning = Math.max(counts.meaning, readCount(candidate.meaning));
    counts.comprehensive = Math.max(counts.comprehensive, readCount(candidate.comprehensive));
  });
  const scalarCandidates = {
    meaning: [source.meaning_count, source.meaning_due_count, source.available_meaning_count],
    comprehensive: [source.comprehensive_count, source.comprehensive_due_count, source.available_comprehensive_count],
  };
  Object.entries(scalarCandidates).forEach(([mode, values]) => {
    values.forEach((value) => {
      if (value != null) {
        found = true;
        counts[mode] = Math.max(counts[mode], readCount(value));
      }
    });
  });
  return found ? counts : null;
};

export const getAvailableTrainingModeCounts = (plan, fallbackItems = []) => {
  const explicit = pickModeCounts(plan);
  if (explicit) return explicit;
  const fallback = countTrainingModes(fallbackItems);
  if (fallback.meaning || fallback.comprehensive || !plan || typeof plan !== 'object') return fallback;
  const legacyTotal = readCount(plan.due_count ?? plan.review_count ?? plan.available_count);
  return { meaning: legacyTotal, comprehensive: 0 };
};

export const filterByTrainingMode = (items, mode) => {
  const normalized = normalizeTrainingMode(mode, null);
  return normalized ? (items || []).filter((item) => hasTrainingMode(getTrainingModes(item), normalized)) : [];
};

export const getCompletedTrainingModes = (item) => {
  const values = item?.completed_training_modes;
  if (!Array.isArray(values)) return [TRAINING_MODE_MEANING];
  if (values.length === 0) return [];
  return [...new Set(values.map((value) => normalizeTrainingMode(value)))];
};

export const hasCompletedTrainingMode = (item, mode) => (
  getCompletedTrainingModes(item).includes(normalizeTrainingMode(mode))
);

export const buildPendingTrainingLaunch = ({ mode, tier, trainingMode } = {}) => ({
  mode: mode === 'review' ? 'review' : 'learn',
  tier: tier || null,
  trainingMode: normalizeTrainingMode(trainingMode),
});
